"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ChevronLeft, ArrowRight, Calendar, MapPin } from "lucide-react";
import Link from "next/link";

const UPCOMING = [
  { date: "FRI · DEC 13", name: "NEON RUSH", sub: "DJ Marka · House", sold: "234 sold", color: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)" },
  { date: "SAT · DEC 14", name: "BIRRIA + BASS", sub: "Live cumbia · 21+", sold: "Sold out", color: "linear-gradient(135deg, #F472B6 0%, #FB923C 100%)" },
  { date: "FRI · DEC 20", name: "ARCADE NIGHT", sub: "Old-school games + free play", sold: "On sale", color: "linear-gradient(135deg, #C4B5FD 0%, #7C3AED 100%)" },
];

export default function MicrositeHero() {
  const { t } = useLang();

  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-[#0A0814] text-white">
      <div
        className="absolute top-1/4 -left-32 w-[800px] h-[800px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.30) 0%, transparent 60%)" }}
      />
      <div
        className="absolute bottom-0 -right-32 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,182,0.20) 0%, transparent 60%)" }}
      />
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto">
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-white/55 hover:text-white transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center"
          style={{ color: "#F472B6" }}
        >
          {t("Venue microsite · Auto-built", "Microsite del venue · Auto-built")}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[40px] sm:text-[56px] md:text-[80px] lg:text-[96px] font-black leading-[0.98] tracking-[-0.04em] mb-6"
        >
          {t("Your venue.", "Tu venue.")}
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #C4B5FD 0%, #F472B6 50%, #FB923C 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("One link. Always current.", "Un link. Siempre al día.")}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-white/60 max-w-[680px] mx-auto leading-relaxed mb-12"
        >
          {t(
            "Public-facing venue page with this weekend's events, branded look, ticket checkout — all auto-built from your account. Mobile-first. Shareable anywhere. Custom domain on Pro.",
            "Página pública del venue con eventos de este weekend, branded, ticket checkout — todo auto-generado desde tu cuenta. Mobile-first. Para compartir donde sea. Dominio custom en Pro."
          )}
        </motion.p>

        {/* Hero: iPhone showing the public microsite */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="flex justify-center mb-12"
        >
          <div className="relative">
            <div
              className="absolute -inset-10 rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(124,58,237,0.30) 0%, transparent 65%)" }}
            />
            <IPhoneFrame>
              <div className="h-full flex flex-col bg-[#0A0814] text-white relative">
                {/* Status */}
                <div className="h-7 flex items-center justify-between px-4 text-[8px] font-bold">
                  <span>11:32</span>
                  <span>5G</span>
                </div>

                {/* URL bar (browser) */}
                <div className="px-3 py-1.5 bg-[#15102A]">
                  <div className="bg-white/10 rounded-full px-3 py-1 text-[8px] text-white/70 truncate text-center">
                    neonclub.com
                  </div>
                </div>

                {/* Hero photo gradient */}
                <div
                  className="h-[140px] relative overflow-hidden flex items-end p-4"
                  style={{
                    background: "linear-gradient(135deg, #7C3AED 0%, #F472B6 60%, #FB923C 100%)",
                  }}
                >
                  <div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    style={{
                      backgroundImage:
                        "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.5) 0%, transparent 50%)",
                    }}
                  />
                  <div className="relative">
                    <div className="text-[8px] font-bold uppercase tracking-widest text-white/75">
                      Dallas, TX · 21+
                    </div>
                    <div className="text-[22px] font-black text-white leading-none tracking-tight">
                      NEON CLUB
                    </div>
                    <div className="text-[8px] font-bold uppercase tracking-wider text-white/85 mt-0.5">
                      {t("Friday & Saturday · Doors 10 PM", "Viernes y Sábado · Puertas 10 PM")}
                    </div>
                  </div>
                </div>

                {/* Upcoming events list */}
                <div className="flex-1 px-3 py-3 space-y-2 overflow-hidden bg-[#0A0814]">
                  <div className="text-[9px] font-bold uppercase tracking-widest text-white/45 mb-1.5">
                    {t("Upcoming", "Próximos")}
                  </div>
                  {UPCOMING.map((ev, i) => (
                    <motion.div
                      key={ev.name}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.9 + 0.12 * i }}
                      className="rounded-[10px] overflow-hidden border border-white/10"
                    >
                      <div className="grid grid-cols-[auto,1fr] gap-2 items-stretch">
                        <div
                          className="px-2 py-2 text-center text-white"
                          style={{ background: ev.color, minWidth: 50 }}
                        >
                          <div className="text-[7px] font-black uppercase tracking-widest leading-tight">
                            {ev.date.split(" · ")[0]}
                          </div>
                          <div className="text-[9px] font-black mt-0.5 leading-none tabular-nums">
                            {ev.date.split(" · ")[1]}
                          </div>
                        </div>
                        <div className="bg-white/[0.04] py-1.5 pr-2">
                          <div className="text-[9px] font-black text-white truncate">{ev.name}</div>
                          <div className="text-[7px] text-white/55 truncate">{ev.sub}</div>
                          <div
                            className="text-[7px] font-bold uppercase tracking-wider mt-0.5"
                            style={{
                              color: ev.sold === "Sold out" ? "#FB923C" : "#F472B6",
                            }}
                          >
                            {ev.sold}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Bottom sticky CTA */}
                <div
                  className="px-3 py-2.5 text-center text-[10px] font-bold text-white"
                  style={{ background: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)" }}
                >
                  {t("Buy tickets →", "Comprar tickets →")}
                </div>
              </div>
            </IPhoneFrame>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="text-center"
        >
          <Link
            href="/signup?vertical=nightlife"
            className="group inline-flex items-center gap-2 text-[15px] font-bold px-8 py-4 rounded-full hover:scale-[1.03] transition-transform shadow-2xl text-white"
            style={{
              background: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)",
              boxShadow: "0 20px 60px rgba(124, 58, 237, 0.5)",
            }}
          >
            {t("Get your venue site live →", "Pon tu sitio en vivo →")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function IPhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-[280px] h-[560px] bg-[#0a0a0a] rounded-[42px] p-[6px] shadow-[0_40px_100px_rgba(0,0,0,0.6)] relative">
      <div className="w-full h-full rounded-[36px] overflow-hidden bg-black relative">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-4 bg-[#0a0a0a] rounded-full z-10" />
        {children}
      </div>
    </div>
  );
}
